// Third party
import {
  DataGridPro, GridApiPro,
  GridCellParams,
  GridColDef,
  useGridApiRef,
  GridActionsCellItemProps,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

// Hooks
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
  FC, MutableRefObject,
  ReactElement, useCallback, useMemo, useState,
} from 'react';
import Card from '@mui/material/Card';

// Assets
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { splittedFullName } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { TLocation } from '../../../types';
import { useTable } from '../../../hooks/useTable';
import { getInviteMembers } from '../../../store/onboarding/selector';
import { MemoizedMultiLocations } from '../../team/components/Table/MultiLocations';
import { MemoizedFilterTabs } from '../../../components/base';
import { TableActions } from '../../../components/table/TableActions';
import { useOptions } from '../../../hooks/useOptions';
import { InviteTeamTableHeader } from './InviteTeamTableHeader';
import { onboardingActions } from '../../../store/onboarding/slice';
import { INDEX_TO_COLOR } from '../../../data';

type TInviteMember = {
  permissionLevel: number;
  locations: Array<TLocation>;
  firstName: string;
  lastName: string;
  email: string;
};

type TInviteTeamList = {
  setMode?: (mode: string) => void
  setFormInputs?: (member: TInviteMember) => void
  setIsOpenInviteMember: (val: boolean) => void
  isOnboarding?: boolean
};

const InviteTeamList: FC<TInviteTeamList> = ({
  setFormInputs = () => {},
  setIsOpenInviteMember,
  setMode = () => {},
  isOnboarding = true,
}) => {
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const dispatch = useAppDispatch();
  const { localText } = useTable(apiRef);
  const [filterStatus, setFilterStatus] = useState('all');

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const teamMembers = useAppSelector(getInviteMembers);
  const { roleMap, roleOptions } = useOptions();

  const { t } = useTranslation(['inputs', 'common', 'team']);

  const getLengthByStatus = (role: number) => teamMembers.filter((member) => member.permissionLevel === role).length;

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoTeamMembers', { ns: 'team' })} />;

  const tabs = roleOptions.map((role, index) => ({
    value: role.value.toString(),
    label: role.name,
    color: INDEX_TO_COLOR[index],
    count: getLengthByStatus(role.value),
  }));

  tabs.unshift({
    value: 'all',
    label: t('all', { ns: 'common' }),
    color: 'info',
    count: teamMembers.length,
  });

  const onAddMember = () => {
    setMode('create');
    setFormInputs({
      locations: [{
        id: 1,
        name: 'Back Office',
      }],
      permissionLevel: 0,
      firstName: '',
      lastName: '',
      email: '',
    });
    setIsOpenInviteMember(true);
  };

  const { pageHeight, resizableDiv } = useScreenResize(59);

  const onCellDoubleClick = (params: GridCellParams) => {
    setFormInputs(params.row);

    setIsOpenInviteMember(true);
  };

  const handleFilterStatus = (newValue: number) => {
    setFilterStatus(newValue.toString());
  };

  const getMemberByFilter = useMemo(() => {
    if (filterStatus === 'all') {
      return teamMembers;
    }
    return teamMembers.filter((member) => member.permissionLevel.toString() === filterStatus);
  }, [filterStatus, teamMembers]);

  const teamColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('name'),
      width: 250,
      renderCell: ({ row }) => {
        const name = isOnboarding ? `${row.firstName} ${row.lastName}` : row.name;

        return (
          <Box display="flex" alignItems="center" height="100%">
            <MemoizedBaseAvatar
              statusTooltipText={t('pendingRegistration', { ns: 'team' })}
              status={row.status}
              avatarFile={row?.avatarFile}
              avatarName={name}
            />
            <Typography variant="caption">
              {startCase(name)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'permissionLevel',
      headerName: t('permission'),
      width: 250,
      renderCell: ({ row }) => {
        const isValidPermission = [0, 1, 2].includes(row.permissionLevel);

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              variant="caption"
              sx={{
                color: isValidPermission ? 'text.primary' : 'error.main',
              }}
            >
              {t(`${roleMap.toKey(row.permissionLevel)}`, { ns: 'common' })}
            </Typography>
            {!isValidPermission && (
              <Typography variant="caption" color="error">
                {t('Invalid permission level. Allowed: 0, 1, 2', { ns: 'common' })}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: t('email'),
      width: 300,
      renderCell: ({ row }) => {
        const emailCount = teamMembers.filter((member) => member.email === row.email).length;
        const isDuplicateEmail = emailCount > 1;

        return (
          <Box
            display="flex"
            {
            ...(isDuplicateEmail && {
              flexDirection: 'column',
            })
          }
            alignItems="center"
            height="100%"
          >
            <Typography
              variant="caption"
              sx={{
                color: isDuplicateEmail ? 'error.main' : 'text.primary',
              }}
            >
              {row.email}
            </Typography>
            {isDuplicateEmail && (
              <Typography variant="caption" color="error">
                {t('Duplicate email detected', { ns: 'common' })}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'locations',
      headerName: t('locations'),
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedMultiLocations locations={row.locations} />
        </Box>
      ),
      valueGetter: (value: Array<TLocation>) => {
        if (Array.isArray(value)) {
          const locationNames = value?.map((location: TLocation) => location.name);

          return locationNames.join(' ,');
        }

        return value;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const deleteMember = () => {
          dispatch(onboardingActions.setInviteMembers(teamMembers.filter((member) => member.email !== row.email)));
        };

        const openTeamNav = () => {
          if (!isOnboarding) {
            const { firstName, lastName } = splittedFullName(row.name);
            setFormInputs({
              ...row,
              firstName,
              lastName,
              permissionLevel: row?.permissionLevel,
              locations: row.locations?.length ? row.locations : [],
            });
          } else {
            setFormInputs(row);
          }
          setMode('edit');
          setIsOpenInviteMember(true);
        };

        return TableActions({
          onConfirm: openTeamNav,
          onDelete: deleteMember,
        }) as ReactElement<GridActionsCellItemProps>[];
      },
    },
  ];

  const renderTableHeader = useCallback(() => (
    <InviteTeamTableHeader isOnboarding={isOnboarding} onClick={onAddMember} />
  ), [apiRef]);

  return (
    <div ref={resizableDiv}>
      <Card sx={{
        height: pageHeight !== 0 ? pageHeight : 'auto',
        pb: 6,
        ...(isMobile && {
          height: 'auto',
          background: 'unset',
          boxShadow: 'unset',
          px: 0,
          pt: 0,
          pb: '35px',
        }),
      }}
      >
        <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
        <DataGridPro
          apiRef={apiRef}
          rows={getMemberByFilter}
          localeText={localText}
          slots={{
            toolbar: renderTableHeader as any,
            loadingOverlay: LinearProgress as any,
            noRowsOverlay: renderCustomNoRowsOverlay,
          }}
          sx={{
            px: 0,
            '.MuiDataGrid-main > *:first-of-type': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: {
                hideFooter: true,
                hideToolbar: true,
              },
            },
          }}
          getRowId={(row) => row.email}
          columns={teamColumns}
          hideFooterPagination
          disableRowSelectionOnClick
          hideFooter
          onCellDoubleClick={onCellDoubleClick}
          checkboxSelection={false}
        />
      </Card>
    </div>
  );
};

export {
  InviteTeamList,
};
